import { LitElement, html, css } from 'lit';

export class AppTutorialModal extends LitElement {
    static styles = css`
        :host {
            display: none;
            position: fixed;
            top: 0; left: 0; width: 100vw; height: 100vh;
            z-index: 100000;
            pointer-events: auto;
        }
        :host([visible]) {
            display: block;
        }

        .overlay {
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(5px);
            display: flex;
            justify-content: center;
            align-items: center;
            animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        .modal {
            background: #ffffff;
            width: 90vw;
            max-width: 1000px;
            height: 85vh;
            border-radius: 12px;
            box-shadow: 0 15px 50px rgba(0,0,0,0.5);
            display: flex;
            overflow: hidden;
            animation: slideUp 0.3s ease-out;
            position: relative;
        }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

        .sidebar {
            width: 280px;
            background: #f8f9fa;
            border-right: 1px solid #e0e0e0;
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
        }

        .sidebar-header {
            padding: 20px;
            background: #fff;
            border-bottom: 1px solid #e0e0e0;
            font-weight: 900;
            color: #2d3436;
            font-size: 1.1rem;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .sidebar-content {
            flex: 1;
            overflow-y: auto;
            padding: 10px 0;
        }

        .category-title {
            font-size: 0.75rem;
            font-weight: bold;
            color: #95a5a6;
            text-transform: uppercase;
            padding: 10px 20px 5px;
            letter-spacing: 0.05em;
        }

        .menu-item {
            padding: 12px 20px;
            cursor: pointer;
            font-size: 0.9rem;
            color: #444;
            transition: all 0.2s;
            border-left: 4px solid transparent;
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .menu-item:hover {
            background: #f1f3f5;
            color: #007bff;
        }
        .menu-item.active {
            background: #e6f2ff;
            color: #007bff;
            border-left-color: #007bff;
            font-weight: bold;
        }
        .menu-item.preparing {
            color: #999;
        }
        
        .menu-item.tour-restart {
            margin-top: 10px;
            color: #e67e22;
            border-left-color: transparent;
        }
        .menu-item.tour-restart:hover {
            background: #fdf2e9;
            color: #d35400;
            border-left-color: #e67e22;
        }

        .main-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            background: #fff;
            overflow-y: auto;
            position: relative;
        }

        .content-header {
            padding: 24px 30px;
            border-bottom: 1px solid #f0f0f0;
        }
        .content-title {
            margin: 0 0 10px 0;
            font-size: 1.4rem;
            color: #2d3436;
        }
        .content-desc {
            margin: 0;
            color: #636e72;
            font-size: 0.95rem;
            line-height: 1.6;
            white-space: pre-wrap;
        }

        .video-container {
            padding: 30px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: #fafafa;
            flex: 1;
        }
        video, iframe {
            width: 100%;
            max-width: 800px;
            max-height: 60vh;
            aspect-ratio: 16 / 9;
            border-radius: 8px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            background: #000;
            border: none;
            outline: none;
        }

        .close-btn {
            position: absolute;
            top: 15px;
            right: 15px;
            width: 36px;
            height: 36px;
            border: none;
            background: #f1f3f5;
            color: #666;
            border-radius: 50%;
            font-size: 20px;
            cursor: pointer;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: all 0.2s;
            z-index: 10;
        }
        .close-btn:hover {
            background: #e74c3c;
            color: #fff;
        }

        .mobile-menu-toggle {
            display: none;
            background: none;
            border: 1px solid #ddd;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 0.9rem;
            font-weight: bold;
            color: #333;
            cursor: pointer;
            margin-bottom: 15px;
            width: 100%;
            text-align: left;
        }

        @media (max-width: 768px) {
            .modal {
                flex-direction: column;
                width: 95vw;
                height: 90vh;
            }
            .sidebar {
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0; left: 0;
                z-index: 5;
                transition: transform 0.3s ease;
                transform: translateX(-100%);
            }
            .sidebar.open {
                transform: translateX(0);
            }
            .mobile-menu-toggle {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            .video-container {
                padding: 15px;
            }
            .content-header {
                padding: 20px 15px 15px;
            }
        }
    `;

    static properties = {
        visible: { type: Boolean, reflect: true },
        activeId: { type: String },
        isSidebarOpenMobile: { type: Boolean }
    };

    constructor() {
        super();
        this.visible = false;
        this.isSidebarOpenMobile = false;

        // GA計測用のプロパティ
        this._viewStartTime = 0;
        this._viewingTutorialId = null;

        this.tutorials = [
            {
                id: 'nav-stack',
                category: 'ナビゲーション',
                title: '基本の画面遷移を作る',
                icon: 'fa-layer-group',
                youtubeId: '9T55svd_ZyM',
                desc: 'ons-navigatorを使った基本的なページ遷移の作り方です。\n「page属性」に最初に表示したいページの「template-id」を指定することで、アプリの入り口を設定します。'
            },
            {
                id: 'nav-tab',
                category: 'ナビゲーション',
                title: 'タブメニューを作る',
                icon: 'fa-window-restore',
                youtubeId: 'nrSAyLJcpGY',
                desc: 'ons-tabbarとons-tabを使って、画面下部で切り替わるタブアプリを作ります。\nそれぞれのタブにpage属性で読み込むテンプレートを指定します。',
            },
            {
                id: 'nav-split',
                category: 'ナビゲーション',
                title: 'サイドメニューを作る',
                icon: 'fa-columns',
                youtubeId : 'gCwh30vc038',
                desc: 'ons-splitterを使って、左から引き出すサイドメニューを作成します。\nons-splitter-sideにメニュー画面を、ons-splitter-contentにメイン画面を指定します。',
            },
            {
                id: 'tips-style',
                category: 'デザイン・装飾',
                title: '余白や色を調整する',
                icon: 'fa-palette',
                desc: 'プロパティパネルのスタイルタブを使って、要素の見た目を調整する方法です。\nmarginやpadding、背景色などを視覚的に変更できます。',
                preparing: true
            },
            {
                id: 'tips-ai',
                category: 'AIエージェント',
                title: 'AIでアプリを作る！',
                icon: 'fa-wand-magic-sparkles',
                desc: 'AIエージェントで「〇〇アプリを作って」「このボタンの色を変えて」など、AIによるアプリの作り方のチュートリアルです。',
                preparing: true
            }
        ];

        this.activeId = this.tutorials[0].id;
    }

    firstUpdated() {
        const fontAwesomeStyle = document.querySelector("link[rel='stylesheet'][data-name='fontawesomev6']");
        if (fontAwesomeStyle) {
            const fontAwesomeStyleClone = fontAwesomeStyle.cloneNode(true);
            fontAwesomeStyleClone.removeAttribute('disabled');
            this.shadowRoot.insertBefore(fontAwesomeStyleClone, this.shadowRoot.firstChild);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        // コンポーネントが破棄される時も時間を送信
        this._sendTutorialDuration();
    }

    show(id = null) {
        if (id && this.tutorials.find(t => t.id === id)) {
            this.activeId = id;
        }
        this.visible = true;
        this.isSidebarOpenMobile = false;

        // 前の計測を終了して送信
        this._sendTutorialDuration();

        // 新しい計測を開始
        this._viewStartTime = Date.now();
        this._viewingTutorialId = this.activeId;

        // 再生位置のリセットのみ行い、自動再生(play)はしないように変更
        setTimeout(() => {
            const video = this.shadowRoot.querySelector('video');
            if (video) {
                video.currentTime = 0;
            }
        }, 100);
    }

    hide() {
        this.visible = false;
        const video = this.shadowRoot.querySelector('video');
        if (video) video.pause();

        const iframe = this.shadowRoot.querySelector('iframe');
        if (iframe) {
            const src = iframe.src;
            iframe.src = '';
            iframe.src = src;
        }

        // 閉じたタイミングで時間を送信
        this._sendTutorialDuration();
    }

    _selectTutorial(id) {
        // 前のタブの滞在時間を送信
        this._sendTutorialDuration();

        this.activeId = id;
        this.isSidebarOpenMobile = false;

        // 新しいタブの計測を開始
        this._viewStartTime = Date.now();
        this._viewingTutorialId = id;

        // 再生位置のリセットのみ行い、自動再生はしない
        setTimeout(() => {
            const video = this.shadowRoot.querySelector('video');
            if (video) {
                video.currentTime = 0;
            }
        }, 50);
    }

    _restartTour() {
        this.hide();
        // カスタムイベントを発行して AppContainer 側でツアーを再起動させる
        window.dispatchEvent(new CustomEvent('restart-tour', { bubbles: true, composed: true }));
    }

    // 滞在時間を計算してGAに送信するメソッド
    _sendTutorialDuration() {
        if (this._viewingTutorialId && this._viewStartTime > 0) {
            const durationSeconds = Math.round((Date.now() - this._viewStartTime) / 1000);
            const tut = this.tutorials.find(t => t.id === this._viewingTutorialId);

            if (tut) {
                // 3秒以上見ていたら送信する（誤クリックでの即閉じは無視）
                if (durationSeconds >= 3 && window.gtag) {
                    window.gtag('event', 'tutorial_viewed', {
                        tutorial_id: tut.id,
                        tutorial_title: tut.title,
                        duration_seconds: durationSeconds
                    });
                }
            }
        }
        this._viewStartTime = 0;
        this._viewingTutorialId = null;
    }

    render() {
        if (!this.visible) return html``;

        const activeTut = this.tutorials.find(t => t.id === this.activeId);
        const categories = [...new Set(this.tutorials.map(t => t.category))];

        return html`
            <div class="overlay" @click=${this.hide}>
                <div class="modal" @click=${e => e.stopPropagation()}>
                    
                    <div class="sidebar ${this.isSidebarOpenMobile ? 'open' : ''}">
                        <div class="sidebar-header">
                            <i class="fa-solid fa-graduation-cap" style="color: #007bff;"></i>
                            チュートリアル & Tips
                            ${this.isSidebarOpenMobile ? html`
                                <button class="close-btn" style="position:static; width:30px; height:30px; margin-left:auto;" @click=${() => this.isSidebarOpenMobile = false}>×</button>
                            ` : ''}
                        </div>
                        <div class="sidebar-content">
                            ${categories.map(cat => html`
                                <div class="category-title">${cat}</div>
                                ${this.tutorials.filter(t => t.category === cat).map(tut => html`
                                    <div class="menu-item ${this.activeId === tut.id ? 'active' : ''} ${tut.preparing ? 'preparing' : ''}"
                                         @click=${() => this._selectTutorial(tut.id)}>
                                        <i class="fa-solid ${tut.icon}" style="width: 20px; text-align:center;"></i>
                                        ${tut.title}
                                        ${tut.preparing ? html`<span style="font-size:0.7em; color:#e74c3c; margin-left: auto;">(準備中)</span>` : ''}
                                    </div>
                                `)}
                            `)}
                            
                            <!-- ツアー再起動ボタン -->
                            <div class="category-title" style="margin-top: 20px;">基本操作</div>
                            <div class="menu-item tour-restart" @click=${this._restartTour}>
                                <i class="fa-solid fa-route" style="width: 20px; text-align:center;"></i>
                                基本の画面構成を見る (ツアー)
                            </div>

                        </div>
                    </div>

                    <div class="main-content">
                        <button class="close-btn" @click=${this.hide}>×</button>
                        <div class="content-header">
                            <button class="mobile-menu-toggle" @click=${() => this.isSidebarOpenMobile = true}>
                                <span><i class="fa-solid fa-bars"></i> メニューを開く</span>
                                <span>${activeTut.title}</span>
                            </button>
                            <h2 class="content-title">${activeTut.title}</h2>
                            <p class="content-desc">${activeTut.desc}</p>
                        </div>
                        <div class="video-container">
                            ${activeTut.preparing ? html`
                                <div style="display:flex; flex-direction:column; align-items:center; color:#95a5a6;">
                                    <i class="fa-solid fa-person-digging" style="font-size: 3em; margin-bottom: 15px;"></i>
                                    <span style="font-size: 1.2em; font-weight: bold;">動画は現在準備中です</span>
                                </div>
                            ` : activeTut.youtubeId ? html`
                                <!-- autoplay=0 に変更し、初期状態を停止にする。enablejsapi=1を追加 -->
                                <iframe src="https://www.youtube.com/embed/${activeTut.youtubeId}?rel=0&enablejsapi=1" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
                            ` : html`
                                <!-- autoplay を削除 -->
                                <video src="${activeTut.videoUrl}" controls muted playsinline loop></video>
                            `}
                        </div>
                    </div>

                </div>
            </div>
        `;
    }
}
customElements.define('app-tutorial-modal', AppTutorialModal);